import { useState } from "react";
import { Link } from "react-router-dom";

const FAQS = [
  {
    q: "Do I need a huge audience to apply?",
    a: "Nope! Seriously, no follower minimums here. We've worked with creators who had 300 subscribers and creators who had 300,000. What we actually care about is that your audience is engaged and that you show up consistently. Quality over quantity, every time.",
  },
  {
    q: "What does it actually cost me?",
    a: "Zero upfront. Not a dollar. We cover design, print production, and distribution — and we recoup that through a revenue share on magazine sales. If your magazine doesn't sell, we don't make money either. That means we're just as motivated as you are to make it great.",
  },
  {
    q: "What kind of content works in print?",
    a: "Way more than you'd think. Written essays, photography, illustration, mixed media, interviews, data journalism, travel diaries, recipes, fitness guides — if it's good content, it works in print. If you're not sure, apply anyway and we'll tell you honestly.",
  },
  {
    q: "How long until my first issue is actually out?",
    a: "Once you're approved, we typically get your first issue designed, printed, and shipped within 4 to 6 weeks. We move fast. You'll be holding your first copy before you know it.",
  },
  {
    q: "Do I have to stop posting online?",
    a: "Absolutely not — keep doing everything you're already doing. The print magazine is additive, not a replacement. Most of our creators actually find their digital audience grows after launching a magazine because it adds serious credibility to their brand.",
  },
  {
    q: "Can I choose how often my magazine comes out?",
    a: "Yes! Weekly, monthly, or quarterly — you pick the cadence that fits your output and your life. We'll help you figure out what makes the most sense for your content and audience. No pressure to over-commit.",
  },
  {
    q: "What if I'm not sure my content will look good in print?",
    a: "That's literally what our design team is here for. We take your content and make it look stunning on the page — you don't need to be a designer or know anything about print layout. Just bring the content, we handle the rest.",
  },
  {
    q: "Is this only for certain niches or topics?",
    a: "No gatekeeping here. Lifestyle, fitness, food, finance, travel, parenting, art, sports, tech, sustainability — we've seen it all and we love the variety. If there are people out there who care about what you make, there's a magazine in it.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-16">
          {/* Left: Sticky label + CTA */}
          <div className="lg:w-80 flex-shrink-0">
            <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-4">
              Real Questions, Real Answers
            </p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-[#2B2B2B] leading-tight mb-5">
              We Know What<br />You&apos;re Thinking
            </h2>
            <p className="text-[#6B6B6B] text-base leading-relaxed mb-8">
              These are the questions we get every single week. Answered honestly, no fluff.
            </p>

            {/* Mini CTA card */}
            <div className="bg-[#F5F1E8] rounded-2xl p-6">
              <p className="font-serif text-xl text-[#2B2B2B] mb-2">Still got questions?</p>
              <p className="text-[#6B6B6B] text-sm leading-relaxed mb-5">
                Reach out — we actually reply. Or just go ahead and apply, and ask us everything on the discovery call.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#C41E3A] text-white text-sm font-semibold whitespace-nowrap hover:bg-[#A0182E] transition-colors cursor-pointer"
                >
                  Apply Now
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#D4C4B0] text-[#C41E3A] text-sm font-semibold whitespace-nowrap hover:bg-[#F0EADF] transition-colors cursor-pointer"
                >
                  Ask Us Directly
                </Link>
              </div>
            </div>
          </div>

          {/* Right: Accordion */}
          <div className="flex-1 flex flex-col divide-y divide-[#EDE8E0]">
            {FAQS.map((faq, i) => (
              <div key={faq.q} className="py-5">
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
                >
                  <span
                    className={`font-serif text-lg leading-snug transition-colors duration-200 ${
                      openIndex === i ? "text-[#C41E3A]" : "text-[#2B2B2B] group-hover:text-[#C41E3A]"
                    }`}
                  >
                    {faq.q}
                  </span>
                  <span
                    className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border transition-all duration-300 mt-0.5 ${
                      openIndex === i
                        ? "bg-[#C41E3A] border-[#C41E3A] text-white rotate-45"
                        : "border-[#D4C4B0] text-[#C41E3A]"
                    }`}
                  >
                    <i className="ri-add-line text-sm" />
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openIndex === i ? "max-h-64 opacity-100 mt-3" : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="text-[#6B6B6B] text-sm leading-relaxed pr-10">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
